function _classPrivateFieldLooseBase(receiver, privateKey) { if (!Object.prototype.hasOwnProperty.call(receiver, privateKey)) { throw new TypeError("attempted to use private field on non-instance"); } return receiver; }
var id = 0;
function _classPrivateFieldLooseKey(name) { return "__private_" + id++ + "_" + name; }
import { assert, deprecate } from '@ember/debug';
import { isStableIdentifier } from '../caches/identifier-cache';
var _store = /*#__PURE__*/_classPrivateFieldLooseKey("store");
var _recordData = /*#__PURE__*/_classPrivateFieldLooseKey("recordData");
var _identifier = /*#__PURE__*/_classPrivateFieldLooseKey("identifier");
var _isDeprecated = /*#__PURE__*/_classPrivateFieldLooseKey("isDeprecated");
/**
 * The RecordDataManager wraps a RecordData cache
 * enforcing that only the public API surface area
 * is exposed.
 *
 * This class is the the return value of both the
 * `recordDataFor` function supplied to the store
 * hook `instantiateRecord`, and the `recordDataFor`
 * method on the `RecordDataStoreWrapper`. It is not
 * directly instantiatable.
 *
 * It handles translating between cache versions when
 * necessary, for instance when a Store is configured
 * to use both a v1 and a v2 cache depending on some
 * heuristic.
 *
 * Starting with the v2 spec, the cache is designed such
 * that it may be implemented as a singleton. However,
 * because the v1 spec was not designed for this whenever
 * we encounter any v1 cache we must wrap all caches, even
 * singletons, in non-singleton managers to preserve v1
 * compatibility.
 *
 * To avoid this performance penalty being paid by all
 * applications, singleton behavior may be opted-in via
 * the configuration supplied to your Ember application
 * at build time. This effectively removes support for
 * v1 caches.
 *
 * ```js
 * let app = new EmberApp(defaults, {
 *   emberData: {
 *     useSingletonManager: true
 *   },
 * });
 * ```
 *
 * @class RecordDataManager
 * @public
 */
export class NonSingletonRecordDataManager {
  get managedVersion() {
    return _classPrivateFieldLooseBase(this, _recordData)[_recordData].version || '1';
  }
  constructor(store, _recordData2, identifier) {
    Object.defineProperty(this, _isDeprecated, {
      value: _isDeprecated2
    });
    this.version = '2';
    Object.defineProperty(this, _store, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _recordData, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _identifier, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldLooseBase(this, _store)[_store] = store;
    _classPrivateFieldLooseBase(this, _recordData)[_recordData] = _recordData2;
    _classPrivateFieldLooseBase(this, _identifier)[_identifier] = identifier;
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](_recordData2)) {
      deprecate(`This RecordData uses the deprecated V1 RecordData Spec. Upgrade to V2 to maintain compatibility.`, false, {
        id: 'ember-data:deprecate-v1-cache',
        until: '5.0',
        since: {
          available: '4.7',
          enabled: '4.7'
        },
        for: 'ember-data'
      });
    }
  }
  // Cache
  // =====

  /**
   * Retrieve the identifier for this v1 recordData
   *
   * DEPRECATED Caches should not be assumed to be 1:1 with resources
   *
   * @method getResourceIdentifier
   * @public
   * @deprecated
   */
  getResourceIdentifier() {
    return _classPrivateFieldLooseBase(this, _identifier)[_identifier];
  }

  /**
   * Push resource data from a remote source into the cache for this identifier
   *
   * @method pushData
   * @public
   * @param identifier
   * @param data
   * @param hasRecord
   * @returns {void | string[]} if `hasRecord` is true then calculated key changes should be returned
   */
  pushData(identifier, data, hasRecord) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    // called by something V1
    if (!isStableIdentifier(identifier)) {
      data = identifier;
      hasRecord = data;
      identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    }
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return recordData.pushData(data, hasRecord);
    }
    return recordData.pushData(identifier, data, hasRecord);
  }

  /**
   * Perform an operation on the cache to update the remote state.
   *
   * Note: currently the only valid operation is a MergeOperation
   * which occurs when a collision of identifiers is detected.
   *
   * @method sync
   * @public
   * @param op the operation to perform
   * @returns {void}
   */
  sync(op) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return;
    }
    recordData.sync(op);
  }

  /**
   * Update resource data with a local mutation. Currently supports operations
   * on relationships only.
   *
   * @method update
   * @public
   * @param operation
   */
  // isCollection is only needed for interop with v1 cache
  update(operation, isResource) {
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](_classPrivateFieldLooseBase(this, _recordData)[_recordData])) {
      const cache = _classPrivateFieldLooseBase(this, _store)[_store]._instanceCache;
      switch (operation.op) {
        case 'addToRelatedRecords':
          _classPrivateFieldLooseBase(this, _recordData)[_recordData].addToHasMany(operation.field, operation.value.map(i => cache.getRecordData(i)), operation.index);
          return;
        case 'removeFromRelatedRecords':
          _classPrivateFieldLooseBase(this, _recordData)[_recordData].removeFromHasMany(operation.field, operation.value.map(i => cache.getRecordData(i)));
          return;
        case 'replaceRelatedRecords':
          _classPrivateFieldLooseBase(this, _recordData)[_recordData].setDirtyHasMany(operation.field, operation.value.map(i => cache.getRecordData(i)));
          return;
        case 'replaceRelatedRecord':
          if (isResource) {
            _classPrivateFieldLooseBase(this, _recordData)[_recordData].setDirtyBelongsTo(operation.field, operation.value ? cache.getRecordData(operation.value) : null);
            return;
          }
          _classPrivateFieldLooseBase(this, _recordData)[_recordData].removeFromHasMany(operation.field, [cache.getRecordData(operation.prior)]);
          _classPrivateFieldLooseBase(this, _recordData)[_recordData].addToHasMany(operation.field, [cache.getRecordData(operation.value)], operation.index);
          return;
        case 'sortRelatedRecords':
          return;
        default:
          return;
      }
    } else {
      _classPrivateFieldLooseBase(this, _recordData)[_recordData].update(operation);
    }
  }

  /**
   * [LIFECYLCE] Signal to the cache that a new record has been instantiated on the client
   *
   * It returns properties from options that should be set on the record during the create
   * process. This return value behavior is deprecated.
   *
   * @method clientDidCreate
   * @public
   * @param identifier
   * @param options
   */
  clientDidCreate(identifier, options) {
    // called by something V1
    if (!isStableIdentifier(identifier)) {
      options = identifier;
      identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    }
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];

    // TODO deprecate return value
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      recordData.clientDidCreate();
      // if a V2 is calling a V1 we need to call both methods
      return recordData._initRecordCreateOptions(options);
    } else {
      return recordData.clientDidCreate(identifier, options);
    }
  }

  /**
   * Pass options to the cache that were supplied to a new record
   * instantiated on the client.
   *
   * DEPRECATED: options are now passed via `clientDidCreate`
   *
   * @method clientDidCreate
   * @public
   * @deprecated
   * @param options
   */
  _initRecordCreateOptions(options) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return recordData._initRecordCreateOptions(options);
    }
  }

  /**
   * [LIFECYCLE] Signals to the cache that a resource
   * will be part of a save transaction.
   *
   * @method willCommit
   * @public
   * @param identifier
   */
  willCommit(identifier) {
    _classPrivateFieldLooseBase(this, _recordData)[_recordData].willCommit(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * [LIFECYCLE] Signals to the cache that a resource
   * was successfully updated as part of a save transaction.
   *
   * @method didCommit
   * @public
   * @param identifier
   * @param data
   */
  didCommit(identifier, data) {
    // called by something V1
    if (!isStableIdentifier(identifier)) {
      data = identifier;
      identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    }
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.didCommit(data) : recordData.didCommit(identifier, data);
  }

  /**
   * [LIFECYCLE] Signals to the cache that a resource
   * was update via a save transaction failed.
   *
   * @method commitWasRejected
   * @public
   * @param identifier
   * @param errors
   */
  commitWasRejected(identifier, errors) {
    _classPrivateFieldLooseBase(this, _recordData)[_recordData].commitWasRejected(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier], errors);
  }

  /**
   * [LIFECYCLE] Signals to the cache that all data for a resource
   * should be cleared.
   *
   * @method unloadRecord
   * @public
   * @param identifier
   */
  unloadRecord(identifier) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      recordData.unloadRecord();
    } else {
      recordData.unloadRecord(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
    }
  }

  // Attrs
  // =====

  /**
   * Retrieve the data for an attribute from the cache
   *
   * @method getAttr
   * @public
   * @param identifier
   * @param propertyName
   * @returns {unknown}
   */
  getAttr(identifier, propertyName) {
    // called by something V1
    if (!isStableIdentifier(identifier)) {
      propertyName = identifier;
      identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    }
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    return _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.getAttr(propertyName) : recordData.getAttr(identifier, propertyName);
  }

  /**
   * Mutate the data for an attribute in the cache
   *
   * @method setAttr
   * @public
   * @param identifier
   * @param propertyName
   * @param value
   */
  setAttr(identifier, propertyName, value) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.setDirtyAttribute(propertyName, value) : recordData.setAttr(identifier, propertyName, value);
  }

  /**
   * Mutate the data for an attribute in the cache
   *
   * DEPRECATED use setAttr
   *
   * @method setDirtyAttribute
   * @public
   * @deprecated
   * @param identifier
   * @param propertyName
   * @param value
   */
  setDirtyAttribute(propertyName, value) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.setDirtyAttribute(propertyName, value) : recordData.setAttr(_classPrivateFieldLooseBase(this, _identifier)[_identifier], propertyName, value);
  }

  /**
   * Query the cache for the changed attributes of a resource.
   *
   * DEPRECATED use changedAttrs
   *
   * @method changedAttributes
   * @public
   * @deprecated
   * @param identifier
   * @returns
   */
  changedAttributes() {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return recordData.changedAttributes();
    }
    return recordData.changedAttrs(_classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for the changed attributes of a resource.
   *
   * @method changedAttrs
   * @public
   * @deprecated
   * @param identifier
   * @returns
   */
  changedAttrs(identifier) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return recordData.changedAttributes();
    }
    return recordData.changedAttrs(identifier);
  }

  /**
   * Query the cache for whether any mutated attributes exist
   *
   * DEPRECATED use hasChangedAttrs
   *
   * @method hasChangedAttributes
   * @public
   * @deprecated
   * @returns {boolean}
   */
  hasChangedAttributes() {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    return _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.hasChangedAttributes() : recordData.hasChangedAttrs(_classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether any mutated attributes exist
   *
   * @method hasChangedAttrs
   * @public
   * @param identifier
   * @returns {boolean}
   */
  hasChangedAttrs(identifier) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    return _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.hasChangedAttributes() : recordData.hasChangedAttrs(identifier);
  }

  /**
   * Tell the cache to discard any uncommitted mutations to attributes
   *
   * DEPRECATED use rollbackAttrs
   *
   * @method rollbackAttributes
   * @public
   * @deprecated
   * @returns
   */
  rollbackAttributes() {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    return _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.rollbackAttributes() : recordData.rollbackAttrs(_classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Tell the cache to discard any uncommitted mutations to attributes
   *
   * @method rollbackAttrs
   * @public
   * @param identifier
   * @returns the names of attributes that were restored
   */
  rollbackAttrs(identifier) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    return _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.rollbackAttributes() : recordData.rollbackAttrs(identifier);
  }

  // Relationships
  // =============

  // the third arg here is "private". In a world with only V2 it is not necessary
  // but in one in which we must convert a call from V2 -> V1 it is required to do this
  // or else to do nasty schema lookup things
  // @runspired has implemented this concept in relationships spikes and is confident
  // we do not need any signal about whether a relationship is a collection or not at this
  // boundary
  /**
   * Query the cache for the current state of a relationship property
   *
   * @method getRelationship
   * @public
   * @param identifier
   * @param propertyName
   * @returns resource relationship object
   */
  getRelationship(identifier, propertyName, isCollection = false) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      let isBelongsTo = !isCollection;
      return isBelongsTo ? recordData.getBelongsTo(propertyName) : recordData.getHasMany(propertyName);
    }
    return recordData.getRelationship(identifier, propertyName);
  }

  /**
   * Query the cache for the current state of a belongsTo field
   *
   * DEPRECATED use `getRelationship`
   *
   * @method getBelongsTo
   * @public
   * @deprecated
   * @param propertyName
   * @returns single resource relationship object
   */
  getBelongsTo(propertyName) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return recordData.getBelongsTo(propertyName);
    } else {
      let identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
      return recordData.getRelationship(identifier, propertyName);
    }
  }

  /**
   * Query the cache for the current state of a hasMany field
   *
   * DEPRECATED use `getRelationship`
   *
   * @method getHasMany
   * @public
   * @deprecated
   * @param propertyName
   * @returns single resource relationship object
   */
  getHasMany(propertyName) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return recordData.getHasMany(propertyName);
    } else {
      let identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
      return recordData.getRelationship(identifier, propertyName);
    }
  }

  /**
   * Mutate the current state of a belongsTo relationship
   *
   * DEPRECATED use update
   *
   * @method setDirtyBelongsTo
   * @public
   * @deprecated
   * @param propertyName
   * @param value
   */
  setDirtyBelongsTo(propertyName, value) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.setDirtyBelongsTo(propertyName, value) : recordData.update({
      op: 'replaceRelatedRecord',
      record: _classPrivateFieldLooseBase(this, _identifier)[_identifier],
      field: propertyName,
      value: value ? value.getResourceIdentifier() : null
    });
  }

  /**
   * Mutate the current state of a hasMany relationship by adding values
   * An index may optionally be specified which the cache should use for
   * where in the list to insert the records
   *
   * DEPRECATED use update
   *
   * @method addToHasMany
   * @deprecated
   * @public
   * @param propertyName
   * @param value
   * @param idx
   */
  addToHasMany(propertyName, value, idx) {
    const identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.addToHasMany(propertyName, value, idx) : recordData.update({
      op: 'addToRelatedRecords',
      field: propertyName,
      record: identifier,
      value: value.map(v => v.getResourceIdentifier())
    });
  }

  /**
   * Mutate the current state of a hasMany relationship by removing values.
   *
   * DEPRECATED use update
   *
   * @method removeFromHasMany
   * @deprecated
   * @public
   * @param propertyName
   * @param value
   */
  removeFromHasMany(propertyName, value) {
    const identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.removeFromHasMany(propertyName, value) : recordData.update({
      op: 'removeFromRelatedRecords',
      record: identifier,
      field: propertyName,
      value: value.map(v => v.getResourceIdentifier())
    });
  }

  /**
   * Mutate the current state of a hasMany relationship by replacing it entirely
   *
   * DEPRECATED use `setHasMany`
   *
   * @method setDirtyHasMany
   * @public
   * @deprecated
   * @param propertyName
   * @param value
   */
  setDirtyHasMany(propertyName, value) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.setDirtyHasMany(propertyName, value) : recordData.update({
      op: 'replaceRelatedRecords',
      record: _classPrivateFieldLooseBase(this, _identifier)[_identifier],
      field: propertyName,
      value: value.map(rd => rd.getResourceIdentifier())
    });
  }

  // State
  // =============

  /**
   * Update the cache state for the given resource to be marked as locally deleted,
   * or remove such a mark.
   *
   * @method setIsDeleted
   * @public
   * @param identifier
   * @param isDeleted
   */
  setIsDeleted(identifier, isDeleted) {
    if (!isStableIdentifier(identifier)) {
      isDeleted = identifier;
      identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    }
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.setIsDeleted(isDeleted) : recordData.setIsDeleted(identifier, isDeleted);
  }

  /**
   * Query the cache for any validation errors applicable to the given resource.
   *
   * @method getErrors
   * @public
   * @param identifier
   * @returns
   */
  getErrors(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData)[_recordData].getErrors(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether a given resource has any available data
   *
   * @method isEmpty
   * @public
   * @param identifier
   * @returns {boolean}
   */
  isEmpty(identifier) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    return _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.isEmpty?.(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]) || false : recordData.isEmpty(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether a given resource was created locally and not
   * yet persisted.
   *
   * @method isNew
   * @public
   * @param identifier
   * @returns {boolean}
   */
  isNew(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData)[_recordData].isNew(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether a given resource is marked as deleted (but not
   * necessarily persisted yet).
   *
   * @method isDeleted
   * @public
   * @param identifier
   * @returns {boolean}
   */
  isDeleted(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData)[_recordData].isDeleted(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether a given resource has been deleted and that deletion
   * has also been persisted.
   *
   * @method isDeletionCommitted
   * @public
   * @param identifier
   * @returns {boolean}
   */
  isDeletionCommitted(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData)[_recordData].isDeletionCommitted(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }
}
function _isDeprecated2(recordData) {
  let version = recordData.version || '1';
  return version !== this.version;
}
var _recordDatas = /*#__PURE__*/_classPrivateFieldLooseKey("recordDatas");
var _recordData3 = /*#__PURE__*/_classPrivateFieldLooseKey("recordData");
export class SingletonRecordDataManager {
  constructor() {
    Object.defineProperty(this, _recordData3, {
      value: _recordData4
    });
    this.version = '2';
    Object.defineProperty(this, _recordDatas, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldLooseBase(this, _recordDatas)[_recordDatas] = new Map();
  }
  _addRecordData(identifier, recordData) {
    _classPrivateFieldLooseBase(this, _recordDatas)[_recordDatas].set(identifier, recordData);
  }
  // Cache
  // =====

  pushData(identifier, data, hasRecord) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).pushData(identifier, data, hasRecord);
  }
  sync(op) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](op.record).sync(op);
  }
  clientDidCreate(identifier, options) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).clientDidCreate(identifier, options);
  }
  willCommit(identifier) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).willCommit(identifier);
  }
  didCommit(identifier, data) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).didCommit(identifier, data);
  }
  commitWasRejected(identifier, errors) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).commitWasRejected(identifier, errors);
  }
  unloadRecord(identifier) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).unloadRecord(identifier);
  }

  // Attrs
  // =====

  getAttr(identifier, propertyName) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).getAttr(identifier, propertyName);
  }
  setAttr(identifier, propertyName, value) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).setAttr(identifier, propertyName, value);
  }
  changedAttrs(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).changedAttrs(identifier);
  }
  hasChangedAttrs(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).hasChangedAttrs(identifier);
  }
  rollbackAttrs(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).rollbackAttrs(identifier);
  }
  getRelationship(identifier, propertyName) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).getRelationship(identifier, propertyName);
  }
  update(operation) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](operation.record).update(operation);
  }

  // State
  // =============

  setIsDeleted(identifier, isDeleted) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).setIsDeleted(identifier, isDeleted);
  }
  getErrors(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).getErrors(identifier);
  }
  isEmpty(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).isEmpty(identifier);
  }
  isNew(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).isNew(identifier);
  }
  isDeleted(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).isDeleted(identifier);
  }
  isDeletionCommitted(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).isDeletionCommitted(identifier);
  }
}
function _recordData4(identifier) {
  assert(`No RecordData Yet Exists!`, _classPrivateFieldLooseBase(this, _recordDatas)[_recordDatas].has(identifier));
  return _classPrivateFieldLooseBase(this, _recordDatas)[_recordDatas].get(identifier);
}